import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowRight, Boxes, CornerDownLeft, FileText, Plus, Search, Truck, User } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { get } from '../lib/api';
import { useAuth } from '../lib/auth';
import { docLabel, mt } from '../lib/format';
import { MODULES } from '../lib/modules';

interface Item { id: string; group: string; label: ReactNode; hint?: ReactNode; icon: ReactNode; to: string; text: string }

/** Pesquisa global e atalhos. Teclado: ↑ ↓ para escolher, Enter para abrir, Esc para fechar. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { can, session } = useAuth();
  const nav = useNavigate();
  const ref = useRef<HTMLDialogElement>(null);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sel, setSel] = useState(0);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) { setQ(''); setSel(0); d.showModal(); }
    if (!open && d.open) d.close();
  }, [open]);
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 180); return () => clearTimeout(t); }, [q]);

  const search = useQuery({
    queryKey: ['search', debounced], enabled: open && debounced.length >= 2,
    queryFn: () => get<{ documents: any[]; customers: any[]; products: any[]; suppliers: any[] }>(`/search?q=${encodeURIComponent(debounced)}`),
  });

  const items = useMemo<Item[]>(() => {
    const actions: Item[] = [
      { id: 'a-ft', group: 'Criar', label: 'Nova factura', icon: <Plus />, to: '/documentos/novo?tipo=FT', text: 'nova factura criar', perm: 'documents:create' },
      { id: 'a-fr', group: 'Criar', label: 'Nova factura-recibo', icon: <Plus />, to: '/documentos/novo?tipo=FR', text: 'factura recibo', perm: 'documents:create' },
      { id: 'a-vd', group: 'Criar', label: 'Nova venda a dinheiro', icon: <Plus />, to: '/documentos/novo?tipo=VD', text: 'venda dinheiro talão', perm: 'documents:create' },
      { id: 'a-pf', group: 'Criar', label: 'Nova pró-forma', icon: <Plus />, to: '/documentos/novo?tipo=PF', text: 'proforma cotação orçamento', perm: 'documents:create' },
      { id: 'a-re', group: 'Criar', label: 'Novo recibo', icon: <Plus />, to: '/documentos/novo?tipo=RE', text: 'recibo recebimento pagamento', perm: 'documents:create' },
      { id: 'a-cl', group: 'Criar', label: 'Novo cliente', icon: <Plus />, to: '/clientes?novo=1', text: 'novo cliente', perm: 'customers:create' },
      { id: 'a-ar', group: 'Criar', label: 'Novo artigo', icon: <Plus />, to: '/artigos?novo=1', text: 'novo artigo produto serviço', perm: 'products:create' },
      { id: 'a-ff', group: 'Criar', label: 'Registar factura de fornecedor', icon: <Plus />, to: '/compras/nova', text: 'compra fornecedor factura', perm: 'purchases:create' },
    ].filter((a) => can(a.perm as never)).map(({ perm: _p, ...a }) => a);
    const pages: Item[] = MODULES.filter((m) => !m.platform || session?.user.is_platform_admin).flatMap((m) => m.groups.flatMap((g) => g.items.filter((i) => !i.perm || can(i.perm)).map((i) => ({
      id: `p-${i.to}`, group: 'Ir para', label: <>{m.label} <ArrowRight className="inline size-3 opacity-50" /> {i.label}</>, icon: <m.icon />, to: i.to, text: `${m.label} ${i.label}`.toLowerCase(),
    }))));
    const term = q.trim().toLowerCase();
    const local = [...actions, ...pages].filter((i) => !term || i.text.includes(term)).slice(0, term ? 8 : 12);
    const r = search.data;
    const remote: Item[] = r && debounced.length >= 2 ? [
      ...r.documents.map((d) => ({ id: `d-${d.id}`, group: 'Documentos', label: d.full_number ?? `${docLabel(d.doc_type)} (rascunho)`, hint: <>{d.customer_name} · {mt(d.gross_total)}</>, icon: <FileText />, to: `/documentos/${d.id}`, text: '' })),
      ...r.customers.map((c) => ({ id: `c-${c.id}`, group: 'Clientes', label: c.name, hint: c.nuit ? `NUIT ${c.nuit}` : c.code, icon: <User />, to: `/clientes/${c.id}`, text: '' })),
      ...r.products.map((p) => ({ id: `a-${p.id}`, group: 'Artigos', label: p.name, hint: <>{p.code} · {mt(p.unit_price)}</>, icon: <Boxes />, to: `/artigos/${p.id}`, text: '' })),
      ...r.suppliers.map((s) => ({ id: `f-${s.id}`, group: 'Fornecedores', label: s.name, hint: s.nuit ? `NUIT ${s.nuit}` : '', icon: <Truck />, to: `/fornecedores/${s.id}`, text: '' })),
    ] : [];
    return [...remote, ...local];
  }, [q, debounced, search.data, can]);

  useEffect(() => setSel(0), [items.length, q]);
  const go = (i?: Item) => { if (!i) return; onClose(); nav(i.to); };

  let lastGroup = '';
  return (
    <dialog ref={ref} onClose={onClose} onMouseDown={(e) => e.target === ref.current && onClose()}
      className="surface mx-auto mt-[12vh] w-[calc(100%-1.5rem)] max-w-2xl overflow-hidden rounded-2xl p-0 text-[var(--text)] shadow-2xl">
      {open && (
        <div onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
          if (e.key === 'Enter') { e.preventDefault(); go(items[sel]); }
        }}>
          <div className="flex items-center gap-3 border-b border-[var(--border)] px-4">
            <Search className="size-5 text-[var(--muted)]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar ou escrever um comando…" aria-label="Pesquisar"
              className="h-14 flex-1 bg-transparent text-[15px] outline-none placeholder:text-[var(--muted)]" />
            {search.isFetching && <span className="text-xs text-[var(--muted)]">a pesquisar…</span>}
          </div>
          <ul className="max-h-[55vh] overflow-y-auto p-2" role="listbox">
            {items.length === 0 && <li className="px-3 py-8 text-center text-sm text-[var(--muted)]">Sem resultados para “{q}”.</li>}
            {items.map((it, idx) => {
              const header = it.group !== lastGroup ? (lastGroup = it.group) : null;
              return (
                <li key={it.id}>
                  {header && <div className="px-3 pt-3 pb-1 text-[11px] font-semibold tracking-wider text-[var(--muted)] uppercase">{header}</div>}
                  <button type="button" role="option" aria-selected={idx === sel} onMouseEnter={() => setSel(idx)} onClick={() => go(it)}
                    className={clsx('flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm', idx === sel ? 'bg-brand-500/10 text-brand-900 dark:text-brand-50' : '')}>
                    <span className="text-[var(--muted)] [&>svg]:size-4">{it.icon}</span>
                    <span className="flex-1 truncate font-medium">{it.label}</span>
                    {it.hint && <span className="truncate text-xs text-[var(--muted)]">{it.hint}</span>}
                    {idx === sel && <CornerDownLeft className="size-3.5 text-[var(--muted)]" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex gap-4 border-t border-[var(--border)] bg-[var(--surface-2)] px-4 py-2 text-[11px] text-[var(--muted)]">
            <span><kbd className="font-semibold">↑ ↓</kbd> navegar</span><span><kbd className="font-semibold">Enter</kbd> abrir</span><span><kbd className="font-semibold">Esc</kbd> fechar</span>
          </div>
        </div>
      )}
    </dialog>
  );
}
